import { Injectable } from '@angular/core';
import { NgForage } from 'ngforage';
import { Observable, Subject } from 'rxjs';
import semaphore from 'semaphore';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import SemaphoreHelper from 'src/app/helpers/semaphore-helper';

@Injectable({
  providedIn: 'root'
})
export class ImmoIPImmoTaskSubmitQueueService {
  private readonly toSubmitStorageKey = 'immoTaskToSubmit';
  private readonly toSubmitGetSemaphore = semaphore(1);
  private readonly toSubmitChange = new Subject<never>();

  constructor(private ngForage: NgForage) {}

  public async getToSubmit(): Promise<ImmoTaskSubmitEntry[]> {
    let toReturn: ImmoTaskSubmitEntry[] = null;
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      toReturn = await this.ngForage.getItem(this.toSubmitStorageKey);
    });
    return toReturn;
  }

  public async submitTypeIsPresentForImmoTask(
    immoTaskId: number,
    submitType: ImmoTaskSubmitType
  ): Promise<boolean> {
    let toReturn: boolean = false;
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const entries = await this.ngForage.getItem<ImmoTaskSubmitEntry[]>(
        this.toSubmitStorageKey
      );
      toReturn =
        CollectionHelper.isPopulated(entries) &&
        entries.some(
          e => e.immoTaskId === immoTaskId && e.submitTypes.includes(submitType)
        );
    });
    return toReturn;
  }

  public async addToSubmit(
    dateString: string,
    immoTaskId: number,
    submitType: ImmoTaskSubmitType
  ): Promise<void> {
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      let toSubmit: ImmoTaskSubmitEntry[] = await this.ngForage.getItem(
        this.toSubmitStorageKey
      );
      toSubmit = toSubmit != null ? toSubmit : [];
      const earlierItem = toSubmit.find(e => e.immoTaskId === immoTaskId);
      if (earlierItem == null) {
        toSubmit.push({
          dateString: dateString,
          immoTaskId: immoTaskId,
          submitTypes: [submitType]
        });
      } else if (this.shouldAddToSubmitTypes(earlierItem, submitType)) {
        earlierItem.submitTypes.push(submitType);
        if (submitType === ImmoTaskSubmitType.FULL) {
          CollectionHelper.filterInPlace(
            earlierItem.submitTypes,
            t =>
              t === ImmoTaskSubmitType.FULL ||
              t === ImmoTaskSubmitType.DELETE_ASSETS
          );
        }
      }
      await this.ngForage.setItem(this.toSubmitStorageKey, toSubmit);
      this.toSubmitChange.next();
    });
  }

  private shouldAddToSubmitTypes(
    earlierItem: ImmoTaskSubmitEntry,
    toWriteSubmitType: ImmoTaskSubmitType
  ): boolean {
    if (toWriteSubmitType === ImmoTaskSubmitType.FULL) {
      return !earlierItem.submitTypes.includes(ImmoTaskSubmitType.FULL);
    }
    return (
      (toWriteSubmitType === ImmoTaskSubmitType.DELETE_ASSETS ||
        !earlierItem.submitTypes.includes(ImmoTaskSubmitType.FULL)) &&
      !earlierItem.submitTypes.includes(toWriteSubmitType)
    );
  }

  public async removeToSubmit(immoTaskId: number): Promise<void> {
    if (immoTaskId == null) {
      return;
    }
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const toSubmit: ImmoTaskSubmitEntry[] = await this.ngForage.getItem(
        this.toSubmitStorageKey
      );
      if (!CollectionHelper.isPopulated(toSubmit)) {
        return;
      }
      CollectionHelper.filterInPlace(
        toSubmit,
        e => e.immoTaskId !== immoTaskId
      );
      await this.ngForage.setItem(this.toSubmitStorageKey, toSubmit);
    });
  }

  public async removeTypeFromSubmitEntry(
    immoTaskId: number,
    toDeleteSubmitType: ImmoTaskSubmitType
  ): Promise<void> {
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const toSubmit: ImmoTaskSubmitEntry[] = await this.ngForage.getItem(
        this.toSubmitStorageKey
      );
      if (!CollectionHelper.isPopulated(toSubmit)) {
        return;
      }
      const matchingSubmitEntry = toSubmit.find(
        e => e.immoTaskId === immoTaskId
      );
      if (matchingSubmitEntry != null) {
        CollectionHelper.filterInPlace(
          matchingSubmitEntry.submitTypes,
          t => t !== toDeleteSubmitType
        );
        await this.ngForage.setItem(this.toSubmitStorageKey, toSubmit);
      }
    });
  }

  public getToSubmitChangeObservable(): Observable<never> {
    return this.toSubmitChange;
  }
}

export interface ImmoTaskSubmitEntry {
  dateString: string;
  immoTaskId: number;
  submitTypes: ImmoTaskSubmitType[];
}

export enum ImmoTaskSubmitType {
  FULL,
  MODIFY_FEEDBACK,
  RESET_NOT_EXECUTED,
  SAVE_EXECUTION_ASSETS,
  DELETE_ASSETS
}
